import React, { useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CreateIcon from '@material-ui/icons/Create';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import ReplayIcon from '@material-ui/icons/Replay';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Alert from '@material-ui/lab/Alert';
import Timeline from '@material-ui/lab/Timeline';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import { useSnackbar } from 'notistack';

import { api } from '../../services';

const TIPOS = {
  criado: { rotulo: 'Documento gerado', cor: '#0288d1', Icone: NoteAddIcon },
  arquivo_visualizado: {
    rotulo: 'Arquivo enviado aberto',
    cor: '#757575',
    Icone: VisibilityIcon,
  },
  visualizado: { rotulo: 'PDF aberto', cor: '#757575', Icone: VisibilityIcon },
  lido: { rotulo: 'Marcado como lido', cor: '#2e7d32', Icone: DoneAllIcon },
  assinado: { rotulo: 'Assinado', cor: '#2e7d32', Icone: CreateIcon },
  enviado: {
    rotulo: 'Arquivo enviado',
    cor: '#0288d1',
    Icone: CloudUploadIcon,
  },
  reemitido: { rotulo: 'PDF reemitido', cor: '#ed6c02', Icone: ReplayIcon },
  arquivo_substituido: {
    rotulo: 'Arquivo substituído',
    cor: '#ed6c02',
    Icone: ReplayIcon,
  },
  assinante_trocado: {
    rotulo: 'Assinante trocado',
    cor: '#ed6c02',
    Icone: SwapHorizIcon,
  },
  forma_assinatura_alterada: {
    rotulo: 'Assinatura por testemunhas definida',
    cor: '#ed6c02',
    Icone: GroupAddIcon,
  },
  aprovado: {
    rotulo: 'Arquivo aprovado',
    cor: '#2e7d32',
    Icone: CheckCircleIcon,
  },
  rejeitado: {
    rotulo: 'Arquivo rejeitado',
    cor: '#c62828',
    Icone: HighlightOffIcon,
  },
  cancelado: { rotulo: 'Cancelado', cor: '#c62828', Icone: CancelIcon },
  excluido: { rotulo: 'Excluído', cor: '#c62828', Icone: CancelIcon },
};

const formatarDataHora = (valor) => new Date(valor).toLocaleString('pt-BR');

// Junta aberturas de PDF seguidas, do mesmo usuário, num só item (com a
// quantidade e o intervalo). Os demais eventos ficam como estão.
const agruparAberturas = (eventos) =>
  eventos.reduce((grupos, evento) => {
    const anterior = grupos[grupos.length - 1];
    if (
      ['visualizado', 'arquivo_visualizado'].includes(evento.tipo) &&
      anterior &&
      anterior.tipo === evento.tipo &&
      anterior.usuario === evento.usuario
    ) {
      anterior.quantidade += 1;
      anterior.ultimo = evento.created_at;
      return grupos;
    }
    return [...grupos, { ...evento, quantidade: 1, ultimo: evento.created_at }];
  }, []);

// Trilha de eventos do documento (somente leitura), mostrada na aba do diálogo de
// detalhes. Carregada só quando a aba é aberta.
const TrilhaEventos = ({ documentoId, onErro }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dados, setDados] = useState(null);
  const [mostrarTodas, setMostrarTodas] = useState(false);

  useEffect(() => {
    setDados(null);
    if (!documentoId) return undefined;
    let ativo = true;
    api
      .get(`/documentos/${documentoId}/eventos`)
      .then((response) => ativo && setDados(response.data.data))
      .catch((e) => {
        if (!ativo) return;
        enqueueSnackbar(
          (e.response && e.response.data && e.response.data.message) ||
            'Erro ao carregar a trilha de eventos.',
          { variant: 'error' },
        );
        onErro();
      });
    return () => {
      ativo = false;
    };
  }, [documentoId]);

  if (!dados) return <Typography variant="body2">Carregando...</Typography>;

  const itens = mostrarTodas
    ? dados.eventos.map((evento) => ({
        ...evento,
        quantidade: 1,
        ultimo: evento.created_at,
      }))
    : agruparAberturas(dados.eventos);
  const temAberturasAgrupadas = itens.some((item) => item.quantidade > 1);

  return (
    <>
      {dados.eventos.length === 0 ? (
        <Alert severity="warning">Documento sem eventos registrados.</Alert>
      ) : dados.integra ? (
        <Alert severity="success">
          Trilha íntegra: nenhum evento foi alterado.
        </Alert>
      ) : (
        <Alert severity="error">
          {`A trilha quebra no evento #${dados.evento_quebrado_id}: o histórico pode ter sido alterado.`}
        </Alert>
      )}
      {(temAberturasAgrupadas || mostrarTodas) && (
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Switch
              size="small"
              color="primary"
              checked={mostrarTodas}
              onChange={(e) => setMostrarTodas(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Mostrar todas as aberturas de PDF
            </Typography>
          }
        />
      )}
      <Timeline align="left" style={{ padding: 0, marginTop: 8 }}>
        {itens.map((evento, indice) => {
          const { rotulo, cor, Icone } = TIPOS[evento.tipo] || {
            rotulo: evento.tipo,
            cor: '#757575',
            Icone: VisibilityIcon,
          };
          return (
            <TimelineItem key={evento.id}>
              <TimelineOppositeContent style={{ flex: 0.35, paddingTop: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  {formatarDataHora(evento.created_at)}
                  {evento.quantidade > 1 &&
                    ` a ${formatarDataHora(evento.ultimo)}`}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot style={{ backgroundColor: cor }}>
                  <Icone fontSize="small" />
                </TimelineDot>
                {indice < itens.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {evento.quantidade > 1
                    ? `${rotulo} ${evento.quantidade} vezes`
                    : rotulo}
                </Typography>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {[evento.usuario, evento.ip && `IP ${evento.ip}`]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                {evento.detalhes && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                  >
                    {evento.detalhes}
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </>
  );
};

export default TrilhaEventos;
